import { PromiseHolder } from 'src/app/shared/utils/promise-holder';
import { isOperatorEventName } from '../analytics/event-names';

export enum OperatorMessageName {
  GET_SDK_VERSION = 'operator_get_sdk_version',
  SET_SDK_VERSION = 'operator_set_sdk_version',
  GET_CONFIG = 'operator_get_config',
  SET_CONFIG = 'operator_set_config',
  GET_TOKEN = 'operator_get_token',
  SET_TOKEN = 'operator_set_token',
  GET_BALANCE = 'operator_get_balance',
  SET_BALANCE = 'operator_set_balance',
  DEMO_BALANCE_UPDATE = 'operator_demo_balance_update',
  REDIRECT = 'operator_redirect',
  PAY_INVOICE = 'operator_pay_invoice',
  CANCEL_INVOICE = 'operator_cancel_invoice',
  HANDLE_PAYMENT_ERROR = 'operator_handle_payment_error',
  PAYMENT_ERROR_HANDLED = 'operator_payment_error_handled',
  ANALYTICS_EVENT = 'operator_analytics_event',
}

interface OperatorMessage {
  message: string;
  data?: object;
}

export enum PaymentErrorAction {
  Retry = 'RETRY',
  GenericError = 'GENERIC_ERROR',
  OperatorError = 'OPERATOR_ERROR',
  IGNORE = 'IGNORE',
}

interface PaymentErrorData {
  action: PaymentErrorAction;
  message?: string;
}

export class OperatorMessenger {
  private paymentErrorPromiseHolder?: PromiseHolder<PaymentErrorData>;

  constructor(
    private sdkVersionCallback: (sdkVersion: string) => void,
    private tokenCallback: (token: string) => Promise<void>,
    private configCallback: (config: object) => void,
    private balanceCallback: (balance: number, currency: string) => void,
    private cancelInvoiceCallback: (invoice: string) => void,
  ) {
    this.connectToOperator();
  }

  sendPaymentError(error: unknown) {
    if (this.paymentErrorPromiseHolder) {
      this.paymentErrorPromiseHolder.reject(new Error('Operator error overridden'));
      this.paymentErrorPromiseHolder = undefined;
    }
    this.paymentErrorPromiseHolder = new PromiseHolder<PaymentErrorData>();
    this.paymentErrorPromiseHolder.init();
    OperatorMessenger.postMessage(
      { message: OperatorMessageName.HANDLE_PAYMENT_ERROR, data: { error } },
      '*',
    );

    return this.paymentErrorPromiseHolder.getPromise();
  }

  static sendAnalyticsEventToOperator(eventName: string): void {
    if (!isOperatorEventName(eventName)) {
      return;
    }

    try {
      const message = {
        message: OperatorMessageName.ANALYTICS_EVENT,
        data: {
          eventName,
        },
      };

      window.parent.postMessage(JSON.stringify(message), '*');
    } catch (error) {
      console.error('Failed to send analytics event to operator', error);
    }
  }

  static sendMessage(message: OperatorMessageName, data?: object): void {
    OperatorMessenger.postMessage({ message, data }, '*');
  }

  private connectToOperator() {
    window.addEventListener('message', this.receiveMessage.bind(this), false);
  }

  static postMessage(message: OperatorMessage, origin: string): void {
    const messageObjectJSON = JSON.stringify(message);
    window.parent.postMessage(messageObjectJSON, origin);
  }

  private async receiveMessage(event: MessageEvent): Promise<void> {
    try {
      if (typeof event.data === 'string' && this.isValidJSON(event.data)) {
        const messageObject = JSON.parse(event.data);

        if (messageObject.message === OperatorMessageName.SET_TOKEN) {
          await this.tokenCallback(messageObject.token);
        } else if (messageObject.message === OperatorMessageName.SET_CONFIG) {
          this.configCallback(messageObject.config);
        } else if (messageObject.message === OperatorMessageName.SET_BALANCE) {
          this.balanceCallback(messageObject.balance, messageObject.currency);
        } else if (messageObject.message === OperatorMessageName.CANCEL_INVOICE) {
          this.cancelInvoiceCallback(messageObject.invoice);
        } else if (messageObject.message === OperatorMessageName.PAYMENT_ERROR_HANDLED) {
          this.paymentErrorPromiseHolder?.resolve(messageObject.data);
        } else if (messageObject.message === OperatorMessageName.SET_SDK_VERSION) {
          this.sdkVersionCallback(messageObject.version);
        }
      }
    } catch (error) {
      console.error('CLINCH: Failed to parse message from operator', error);
    }
  }

  private isValidJSON(data: string): boolean {
    try {
      JSON.parse(data);
      return true;
    } catch {
      return false;
    }
  }
}
